import React, { useEffect, useMemo } from 'react';

import { useBreakPoints } from 'core';
import { useListUserPurchases } from 'store/Api';
import {
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  NoDataMessageCard,
  NoResults,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';
import { useInViewport } from 'ui/Hooks';

import { useUserPurchasesSearchContext } from '../PurchaseSearchContext';
import { ExportPurchasesModalButton } from './ExportPurchasesModal';
import { PurchaseDetailCard } from './PurchaseDetailCard';
import { PurchaseSearchInput } from './PurchaseSearchInput';
import { PurchasesFilterModal } from './PurchasesFilterModal';

const PurchasesList = ({ isPurchasesEmpty }: { isPurchasesEmpty: boolean }) => {
  const { ref, inViewport } = useInViewport();
  const ctx = useUserPurchasesSearchContext();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useListUserPurchases({
    queryOptions: {
      ...ctx.UserPurchasesSearchObject,
      searches: {
        ...ctx.UserPurchasesSearchObject?.searches,
        'store_items.type': {
          value: 'StoreItems::DiscountAmount,StoreItems::DiscountPercent',
          by: 'not_in',
        },
      },
      join: 'AND',
    },
  });

  useEffect(() => {
    if (inViewport && hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, inViewport]);

  const isSearching = useMemo(() => {
    if (!ctx.UserPurchasesSearchObject?.searches) {
      return false;
    }
    return Object.values(ctx.UserPurchasesSearchObject?.searches).some((search) => search.value);
  }, [ctx.UserPurchasesSearchObject?.searches]);

  return (
    <Stack gap={0}>
      <Skeleton visible={isLoading}>
        {isSearching && (
          <Text fz={14} fw={600} c="gray">
            Search Results
          </Text>
        )}
        {isSearching && !isLoading && !data?.pages[0].cart_items.length && <NoResults />}
        {isPurchasesEmpty && (
          <NoDataMessageCard
            title="No Purchases Yet"
            message="Your donations and store purchases will be displayed here."
          />
        )}
        {data?.pages.map((page) =>
          page.cart_items.map((item) => (
            <React.Fragment key={item.uuid}>
              <PurchaseDetailCard key={item.uuid} cartItem={item} />
              <Divider size="sm" color="gray.0" />
            </React.Fragment>
          ))
        )}
        <Center ref={ref} py="xl" h={1}>
          {isFetchingNextPage && (
            <Group>
              <Loader />
              <Text fz="lg" fw={500} c="gray.2">
                Loading...
              </Text>
            </Group>
          )}
        </Center>
      </Skeleton>
    </Stack>
  );
};

const PurchasesContent = ({ isPurchasesEmpty }: { isPurchasesEmpty: boolean }) => {
  const { isSmallScreen } = useBreakPoints();
  const shouldShowExportButton = useMemo(
    () => !isPurchasesEmpty && !isSmallScreen,
    [isSmallScreen, isPurchasesEmpty]
  );
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Purchases</Title>
        <Flex gap="md">
          <PurchasesFilterModal isPurchasesEmpty={isPurchasesEmpty} />
          {shouldShowExportButton && <ExportPurchasesModalButton />}
        </Flex>
      </Group>
      <PurchaseSearchInput isPurchasesEmpty={isPurchasesEmpty} />
      <PurchasesList isPurchasesEmpty={isPurchasesEmpty} />
    </Stack>
  );
};

export { PurchasesContent };
